<script lang="ts">
  /**
   * The sheet behind a game's menu button: everything that is not playing.
   * Back to the game, (in a room) the room's code with a way to share it, How
   * to play, Settings, and then, in a section of its own under "Stop
   * playing", the way out, in the danger tone. Ending the game never reads as
   * the next row after Settings.
   *
   * The same component serves a solo run and a room; the words of the way out
   * are the caller's ("Quit run", "Leave room"), and so is what it does: this
   * only closes itself and reports the press, and the screen asks its own
   * "are you sure" through the back guard it already has.
   *
   * The parent owns `open` (`docs/decisions/0047`). Every row closes the
   * sheet first, so whatever it opens is never stacked on a menu.
   */
  import { Icon, OptionRow, OptionSection, PillButton, Sheet } from '@artibens/app-shell';

  const {
    open,
    onclose,
    onHowToPlay,
    onSettings,
    exitTitle,
    exitDetail,
    onExit,
    exitTestid,
    room,
  }: {
    open: boolean;
    onclose: () => void;
    onHowToPlay: () => void;
    onSettings: () => void;
    /** The words of the way out: "Quit run", "Leave room". */
    exitTitle: string;
    exitDetail: string;
    onExit: () => void;
    exitTestid?: string | undefined;
    /** In a room: its code, and what the Share pill does. */
    room?: { readonly code: string; readonly onShare: () => void } | undefined;
  } = $props();

  function pick(action: () => void): void {
    onclose();
    action();
  }
</script>

{#snippet resumeIcon()}<Icon name="play" size={20} />{/snippet}
{#snippet codeIcon()}<Icon name="code" size={20} />{/snippet}
{#snippet helpIcon()}<Icon name="book" size={20} />{/snippet}
{#snippet settingsIcon()}<Icon name="gear" size={20} />{/snippet}
{#snippet exitIcon()}<Icon name="exit" size={20} />{/snippet}

<Sheet {open} title="Game menu" {onclose} testid="game-menu">
  <OptionSection>
    <OptionRow
      title="Resume"
      detail="Back to the game"
      icon={resumeIcon}
      testid="game-menu-resume"
      onclick={onclose}
    />
    {#if room !== undefined && room.code !== ''}
      <div class="code-row">
        <div class="code-row-text">
          <OptionRow
            title={`Room ${room.code}`}
            detail="Somebody can still watch with this code"
            icon={codeIcon}
            testid="game-menu-code"
          />
        </div>
        <PillButton variant="light" label="Share" testid="game-menu-share" onclick={room.onShare} />
      </div>
    {/if}
    <OptionRow
      title="How to play"
      detail="The basics of the game"
      icon={helpIcon}
      testid="game-menu-how-to-play"
      onclick={() => {
        pick(onHowToPlay);
      }}
    />
    <OptionRow
      title="Settings"
      detail="Sound, colours, controls"
      icon={settingsIcon}
      testid="game-menu-settings"
      onclick={() => {
        pick(onSettings);
      }}
    />
  </OptionSection>
  <OptionSection label="Stop playing" testid="game-menu-stop">
    <OptionRow
      title={exitTitle}
      detail={exitDetail}
      icon={exitIcon}
      tone="danger"
      testid={exitTestid ?? 'game-menu-exit'}
      onclick={() => {
        pick(onExit);
      }}
    />
  </OptionSection>
</Sheet>

<style>
  .code-row {
    display: flex;
    align-items: center;
    gap: var(--ui-space-2);
  }

  .code-row-text {
    flex: 1 1 auto;
    min-width: 0;
  }
</style>
